/**
 * Sanitizes and validates the given redirect URL. Only relative URLs are expected/supported. If the URL is considered
 * invalid or unsafe, an exception is thrown.
 * @param {string} redirectUrl the decoded but unsanitized and unvalidated redirect URL specified by the user
 * @returns {string} the sanitized and validated redirect URL
 */
export const validateRedirectUrl = (redirectUrl) => {
  if (typeof window === 'undefined' || !window.location || !window.location.origin) {
    throw new Error('Method must be called from a browser environment');
  }

  if (!redirectUrl || typeof redirectUrl !== 'string') {
    throw new Error('Invalid redirect URL specified');
  }

  // this pattern might not support the full range of allowed URIs, but it should be sufficient for 99% of use cases
  const RELATIVE_URI_PATTERN =
    /^(?<path>\/?[\p{Letter}\p{Mark}\d_-])*(?<cgi_file>[\p{Letter}\p{Mark}\d_-]+\.[\p{Letter}\p{Mark}\d_-]+)?\/?(?<query>\?[\p{Letter}\p{Mark}\d=&_-]+)?(?<fragment>#[\p{Letter}\p{Mark}\d\/.,=_-]*)?$/u;

  const sanitized = redirectUrl.trim();
  if (RELATIVE_URI_PATTERN.test(sanitized)) {
    return sanitized;
  }

  throw new Error('Invalid relative redirect URL specified');
};

/**
 * @param {string} redirectUrl the unsanitized and unvalidated redirect URL specified by the user
 * @param {string} fallback optional, defaults to '/'
 * @returns {string} the sanitized and validated redirect URL or the fallback if validation has failed
 * @see validateRedirectUrl
 */
export const getValidatedRedirectUrlOrFallback = (candidate, fallback = '/') => {
  try {
    return validateRedirectUrl(candidate, fallback);
  } catch (e) {
    return fallback;
  }
};
